// Missing version always means legacy, including dynamic and nested layouts.
export const layoutVersion = canvas => canvas?.layoutContractVersion === undefined
  ? 1 : canvas.layoutContractVersion;

export function visitCanvases(value, visitor, path = 'search.views') {
  if (!value || typeof value !== 'object') return;
  for (const [key, child] of Object.entries(value)) {
    if (key === 'canvas') visitor(child, `${path}.canvas`);
    else if (key !== 'nodes' && key !== 'props') visitCanvases(child, visitor, `${path}.${key}`);
  }
}

// Run on writes/conversion, never retroactively on legacy reads.
export function validateLayoutGraph(canvas) {
  if (![1, 2].includes(layoutVersion(canvas))) return 'Unsupported layoutContractVersion';
  if (!canvas || !Array.isArray(canvas.nodes) || !canvas.root) return 'Canvas requires root and nodes';
  const nodes = new Map();
  for (const node of canvas.nodes) {
    if (!node || typeof node.id !== 'string' || !node.id || !node.type) return 'Every node requires id and type';
    if (nodes.has(node.id)) return `Duplicate node '${node.id}'`;
    nodes.set(node.id, node);
  }
  if (!nodes.has(canvas.root)) return `Missing root '${canvas.root}'`;
  const parents = new Set();
  for (const node of nodes.values()) {
    if (node.slots != null && (typeof node.slots !== 'object' || Array.isArray(node.slots))) return `Invalid slots on '${node.id}'`;
    if (node.type === 'columns') {
      const count = node.props?.columns ?? 2;
      if (!Number.isInteger(count) || count < 1 || count > 12) return `Invalid column count on '${node.id}' (1–12)`;
      const slots = Object.keys(node.slots || {});
      if (slots.length !== count || slots.some(key => !Array.from({ length: count }, (_, i) => `col-${i + 1}`).includes(key))) return `Invalid column slots on '${node.id}'`;
      const template = node.props?.template;
      if (layoutVersion(canvas) === 2 && template) {
        if (typeof template !== 'string') return `Invalid template on '${node.id}'`;
        const flat = template.trim().replace(/minmax\(\s*0\s*,\s*([\d.]+fr)\s*\)/g, '$1');
        const repeat = flat.match(/^repeat\(\s*(\d+)\s*,\s*([\d.]+fr)\s*\)$/);
        const tracks = repeat ? Array.from({ length: Math.min(Number(repeat[1]), 13) }, () => repeat[2]) : flat.split(/\s+/);
        const fractions = tracks.every(track => /^\d+(?:\.\d+)?fr$/.test(track) && parseFloat(track) > 0);
        const percentages = Number(node.props.gap || 0) === 0
          && tracks.every(track => /^\d+(?:\.\d+)?%$/.test(track) && parseFloat(track) > 0)
          && Math.abs(tracks.reduce((sum, track) => sum + parseFloat(track), 0) - 100) < 0.000001;
        if (tracks.length !== count || (!fractions && !percentages)) return `Unsupported or mismatched column template on '${node.id}'`;
      }
      const ratio = node.props?.ratio;
      if (!node.props?.template && ratio != null && (!Array.isArray(ratio) || ratio.length !== count || ratio.some(n => !Number.isFinite(Number(n)) || Number(n) <= 0))) return `Invalid ratio on '${node.id}'`;
    }
    for (const [slot, ids] of Object.entries(node.slots || {})) {
      if (!Array.isArray(ids)) return `Invalid slot '${node.id}.${slot}'`;
      for (const id of ids) {
        if (!nodes.has(id)) return `Missing child '${id}' in '${node.id}.${slot}'`;
        if (id === canvas.root || parents.has(id)) return `Cycle or multiple parents at '${id}'`;
        parents.add(id);
      }
    }
  }
  const seen = new Set();
  const pending = [canvas.root];
  while (pending.length) {
    const id = pending.pop();
    if (seen.has(id)) return `Cycle at '${id}'`;
    seen.add(id);
    Object.values(nodes.get(id).slots || {}).forEach(ids => pending.push(...ids));
  }
  return seen.size === nodes.size ? true : 'Unreachable nodes or disconnected cycle';
}

export function structuralShape(canvas) {
  if (!canvas) return null;
  return { root: canvas.root, nodes: canvas.nodes?.map(node => ({
    id: node.id, type: node.type, slots: node.slots,
    ...(node.type === 'columns' ? { columns: node.props?.columns, ratio: node.props?.ratio, template: node.props?.template } : {}),
  })) };
}

export function conversionError(canvas) {
  const graphError = validateLayoutGraph({ ...canvas, layoutContractVersion: 2 });
  if (graphError !== true) return graphError;
  for (const node of canvas.nodes) {
    if (node.props?.className || node.props?.style) return `Cannot convert custom CSS at '${node.id}'. Export the original layout first.`;
    if (node.type === 'columns' && node.props?.template) {
      const template = node.props.template.trim();
      const tracks = template.replace(/minmax\(\s*0\s*,\s*([\d.]+fr)\s*\)/g, '$1');
      if (!(Number(node.props.gap || 0) === 0 && /^(?:[\d.]+%\s*)+$/.test(tracks)) && !/^(?:[\d.]+fr\s*)+$/.test(tracks) && !/^repeat\(\s*\d+\s*,\s*1fr\s*\)$/.test(tracks)) {
        return `Cannot safely convert custom column template at '${node.id}'. The original layout is unchanged.`;
      }
    }
  }
  return null;
}
